import React, { useEffect, useState } from 'react';

import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { CreateRateGroupRuleBody } from '~/services/cruises/RateGroupRuleService';
import { RateGroup, RateGroupRecommendation } from '~/services/cruises/RateGroupService';

interface Props {
  isError: boolean;
  isLoading: boolean;
  isOpen: boolean;
  rateCode: string;
  rateDescription: string | null;
  rateGroups: Array<RateGroup>;
  suggestion?: RateGroupRecommendation;
  suggestedRateGroupId: string;
  onClose: () => void;
  onConfirm: (
    rateGroupId: string,
    filterType: CreateRateGroupRuleBody['filters'][number]['filterType'],
    matchType: CreateRateGroupRuleBody['filters'][number]['matchType'],
    rateCodePattern: string,
  ) => void;
}

function AcceptSuggestionModal({
  isError,
  isLoading,
  isOpen,
  rateCode,
  rateDescription,
  rateGroups,
  suggestion,
  suggestedRateGroupId,
  onClose,
  onConfirm,
}: Props) {
  const [selectedRateGroupId, setSelectedRateGroupId] = useState(suggestedRateGroupId);
  const [filterType, setFilterType] = useState<CreateRateGroupRuleBody['filters'][number]['filterType']>('RATE_CODE');
  const [matchType, setMatchType] = useState<CreateRateGroupRuleBody['filters'][number]['matchType']>('EXACT');
  const [matchValue, setMatchValue] = useState('');
  const selectedRateGroup = rateGroups.find((rateGroup) => rateGroup.id === selectedRateGroupId);

  useEffect(() => {
    if (isOpen) {
      setSelectedRateGroupId(suggestedRateGroupId);
      setFilterType('RATE_CODE');
      setMatchType('EXACT');
      setMatchValue('');
    }
  }, [isOpen, suggestedRateGroupId]);
  const selectedFilterValue = filterType === 'RATE_CODE_DESCRIPTION' ? rateDescription : rateCode;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {suggestion?.rateGroup ? 'Accept rate group suggestion' : 'Assign rate group'}
      </DialogTitle>
      <DialogContent>
        {isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Unable to create the rate group rule.
          </Alert>
        )}
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ boxShadow: 1 }}>
              <CardContent>
                {selectedRateGroup && (
                  <>
                    <Typography fontWeight={700}>{selectedRateGroup.name}</Typography>
                    <Chip
                      label={selectedRateGroup.isRefundable ? 'Refundable' : 'Non-refundable'}
                      color={selectedRateGroup.isRefundable ? 'success' : 'default'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="subtitle2" mt={2}>
                      Description
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRateGroup.description || 'Unavailable'}
                    </Typography>
                    <Typography variant="subtitle2" mt={2}>
                      Terms and conditions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRateGroup.termsAndConditions || 'Unavailable'}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {suggestion && (
              <Card variant="outlined" sx={{ boxShadow: 1, mb: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SmartToyOutlinedIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2">AI suggestion</Typography>
                  </Stack>
                  <Typography fontWeight={700} mt={0.5}>
                    {suggestion.rateGroup?.name}
                  </Typography>
                  <Typography variant="body2" mt={1}>
                    <strong>Confidence:</strong> {suggestion.confidence}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Explanation:</strong> {suggestion.rationale}
                  </Typography>
                </CardContent>
              </Card>
            )}
            <FormControl fullWidth>
              <InputLabel id="suggested-rate-group-label">Rate group</InputLabel>
              <Select
                labelId="suggested-rate-group-label"
                label="Rate group"
                value={selectedRateGroupId}
                onChange={(event) => setSelectedRateGroupId(event.target.value)}
              >
                {rateGroups.map((rateGroup) => (
                  <MenuItem key={rateGroup.id} value={rateGroup.id}>
                    {rateGroup.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="suggested-rate-filter-type-label">Filter type</InputLabel>
              <Select<CreateRateGroupRuleBody['filters'][number]['filterType']>
                labelId="suggested-rate-filter-type-label"
                label="Filter type"
                value={filterType}
                onChange={(event) => setFilterType(event.target.value)}
              >
                <MenuItem value="RATE_CODE">Rate code</MenuItem>
                <MenuItem value="RATE_CODE_DESCRIPTION" disabled={!rateDescription}>
                  Rate code description
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={filterType === 'RATE_CODE_DESCRIPTION' ? 'Rate code description' : 'Rate code'}
              value={selectedFilterValue ?? 'Unavailable'}
              fullWidth
              disabled
              sx={{ mt: 2 }}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="suggested-rate-match-type-label">Match type</InputLabel>
              <Select<CreateRateGroupRuleBody['filters'][number]['matchType']>
                labelId="suggested-rate-match-type-label"
                label="Match type"
                value={matchType}
                onChange={(event) => setMatchType(event.target.value)}
              >
                <MenuItem value="EXACT">Exact</MenuItem>
                <MenuItem value="CONTAINS">Contains</MenuItem>
                <MenuItem value="PREFIX">Prefix</MenuItem>
                <MenuItem value="SUFFIX">Suffix</MenuItem>
              </Select>
            </FormControl>
            {matchType !== 'EXACT' && (
              <TextField
                label="Match value"
                value={matchValue}
                onChange={(event) => setMatchValue(event.target.value)}
                fullWidth
                required
                sx={{ mt: 2 }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onConfirm(
              selectedRateGroupId,
              filterType,
              matchType,
              matchType === 'EXACT' ? (selectedFilterValue ?? '') : matchValue,
            )
          }
          disabled={
            isLoading || !selectedRateGroupId || !selectedFilterValue || (matchType !== 'EXACT' && !matchValue.trim())
          }
        >
          Create rule
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AcceptSuggestionModal;
